import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useEffect, type ReactNode } from 'react';
import { loginRequest, AUTH_BYPASS, MAJANS_TENANT_ID } from '../config/auth';

/**
 * Gates child render on authentication.
 * - If VITE_AUTH_BYPASS=true (local dev), renders children immediately.
 * - If signed in, renders children.
 * - If not signed in, renders sign-in card.
 * - Inflight redirect / silent renewal: renders a brief loading state.
 *
 * Phase 13: when multiple Microsoft accounts are cached in the user's
 * browser (e.g. their Majans work account AND a personal MS account),
 * MSAL picks `accounts[0]` arbitrarily and can issue tokens for the
 * wrong identity. Bug pattern: Matthew's data appears missing in his
 * normal browser but present in incognito (incognito starts with one
 * account only). Fix: explicitly pick the Majans-tenant account and
 * pin it as the active account so all subsequent token requests use it.
 */
export default function AuthGate({ children }: { children: ReactNode }) {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Auto-trigger sign-in once on mount if not signed in (popup, not redirect, to avoid losing app state).
  useEffect(() => {
    if (AUTH_BYPASS) return;
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch(err => {
        console.error('loginRedirect failed:', err);
      });
    }
  }, [isAuthenticated, inProgress, instance]);

  // Phase 13: pin the Majans-tenant account as the active account.
  // Runs every time `accounts` changes — covers fresh login, silent renew,
  // and the case where another tab signs in/out a different account.
  useEffect(() => {
    if (AUTH_BYPASS) return;
    if (!accounts || accounts.length === 0) return;

    const majans = accounts.find(a => a.tenantId === MAJANS_TENANT_ID);
    const current = instance.getActiveAccount();
    if (majans && current?.homeAccountId !== majans.homeAccountId) {
      instance.setActiveAccount(majans);
      // eslint-disable-next-line no-console
      console.log('[AuthGate] active account pinned to Majans tenant', { username: majans.username, oid: majans.localAccountId });
    } else if (!majans && accounts.length > 0) {
      // No Majans account in cache — log loudly so this doesn't fail silently
      // eslint-disable-next-line no-console
      console.warn('[AuthGate] no Majans-tenant account found among cached accounts', accounts.map(a => ({ tenant: a.tenantId, username: a.username })));
    }
  }, [accounts, instance]);

  if (AUTH_BYPASS || isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg, #0f1115)',
        color: 'var(--text-primary, #fafafa)',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: 32,
          maxWidth: 400,
        }}
      >
        <img
          src="/majans-logo.png"
          alt="Majans"
          style={{ width: 56, height: 56, borderRadius: 8, margin: '0 auto 24px' }}
        />
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 8, letterSpacing: '-0.01em' }}>
          Majans Commercial
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted, #9ca3af)', marginBottom: 24 }}>
          {inProgress === InteractionStatus.HandleRedirect || inProgress === InteractionStatus.Startup
            ? 'Signing you in…'
            : 'Sign in with your Majans account to continue.'}
        </p>
        {inProgress === InteractionStatus.None && (
          <button
            onClick={() => instance.loginRedirect(loginRequest)}
            style={{
              background: '#EF3340',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Sign in
          </button>
        )}
      </div>
    </div>
  );
}
